import { useState } from "react";

import { useNavigate } from "react-router-dom";

import { ArrowLeft, Plus } from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import {
  useCreateWarehouse,
  useDeleteWarehouse,
  useUpdateWarehouse,
  useWarehouses,
} from "~/hooks/wms";
import type {
  WarehouseFormData,
  Warehouse as WarehouseType,
} from "~/types/wms";

import { WarehouseDialog } from "./components/WarehouseDialog";
import { WarehousesTab } from "./components/WarehousesTab";

export function LocationsPage() {
  const navigate = useNavigate();

  // Dialog state
  const [warehouseDialogOpen, setWarehouseDialogOpen] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState<
    WarehouseType | undefined
  >();
  const [deleteWarehouse, setDeleteWarehouseTarget] = useState<
    WarehouseType | undefined
  >();

  // Data queries
  const { data: warehouses = [], isLoading: warehousesLoading } =
    useWarehouses();

  // Mutations
  const createWarehouse = useCreateWarehouse();
  const updateWarehouse = useUpdateWarehouse();
  const deleteWarehouseMutation = useDeleteWarehouse();

  // Warehouse handlers
  const handleCreateWarehouse = () => {
    setEditingWarehouse(undefined);
    setWarehouseDialogOpen(true);
  };

  const handleEditWarehouse = (warehouse: WarehouseType) => {
    setEditingWarehouse(warehouse);
    setWarehouseDialogOpen(true);
  };

  const handleSubmitWarehouse = (data: WarehouseFormData) => {
    if (editingWarehouse) {
      updateWarehouse.mutate(
        { id: editingWarehouse.id, data },
        { onSuccess: () => setWarehouseDialogOpen(false) }
      );
    } else {
      createWarehouse.mutate(data, {
        onSuccess: () => setWarehouseDialogOpen(false),
      });
    }
  };

  const confirmDelete = () => {
    if (!deleteWarehouse) return;
    deleteWarehouseMutation.mutate(deleteWarehouse.id, {
      onSuccess: () => setDeleteWarehouseTarget(undefined),
    });
  };

  const handleSelectWarehouse = (id: string) => {
    navigate(`/wms/locations/${id}`);
  };

  return (
    <PageContainer>
      <PageHeader
        title="Bodegas"
        description="Gestión de bodegas y ubicaciones de almacenamiento"
        back={
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 h-9 text-muted-foreground"
            onClick={() => navigate("/wms")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
        }
        actions={
          <Button onClick={handleCreateWarehouse}>
            <Plus className="mr-1 h-4 w-4" />
            Nueva Bodega
          </Button>
        }
      />

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">
            Listado de Bodegas ({warehouses.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <WarehousesTab
            warehouses={warehouses}
            isLoading={warehousesLoading}
            onEdit={handleEditWarehouse}
            onDelete={(w) => setDeleteWarehouseTarget(w)}
            onSelect={handleSelectWarehouse}
          />
        </CardContent>
      </Card>

      {/* Dialogs */}
      <WarehouseDialog
        open={warehouseDialogOpen}
        onOpenChange={setWarehouseDialogOpen}
        warehouse={editingWarehouse}
        onSubmit={handleSubmitWarehouse}
        isLoading={createWarehouse.isPending || updateWarehouse.isPending}
      />

      {/* Delete confirmation */}
      <AlertDialog
        open={!!deleteWarehouse}
        onOpenChange={(open) => !open && setDeleteWarehouseTarget(undefined)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Bodega</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Está seguro de que desea eliminar la bodega "
              {deleteWarehouse?.name}"? Esta acción eliminará también todos los
              pasillos, racks y posiciones contenidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}
